import {
  categoryAppliesToDraftItemType,
  normalizeProductCategoryId,
  type CategoryAppliesTo,
  type CategoryRecord,
} from './categoryTypes';

export type CategoryOption = {
  id: string;
  label: string;
  description?: string | null;
  color?: string | null;
  disabled?: boolean;
  appliesTo?: CategoryAppliesTo;
  isRestricted?: boolean;
  /** Id de la categoría padre si es subcategoría (para mostrar jerarquía en pickers). */
  parentId?: string | null;
};

export type CategoryFilterOption = {
  id: string | 'all';
  label: string;
  appliesTo?: CategoryAppliesTo;
};

/** Texto para chips, pastillas y accesibilidad (valores de `categories.applies_to`). */
export const categoryAppliesToShortLabel = (appliesTo: CategoryAppliesTo): string => {
  switch (appliesTo) {
    case 'product':
      return 'Productos';
    case 'service':
      return 'Servicios';
    default:
      return 'Productos y servicios';
  }
};

type CategorySource = Pick<CategoryRecord, 'id' | 'name' | 'description' | 'color' | 'isActive' | 'appliesTo' | 'isRestricted' | 'parentId'>;

type BuildCategoryOptionsParams = {
  fallbackCategories?: CategorySource[];
  includeAll?: boolean;
  allLabel?: string;
  allColor?: string;
  /** Si se define, solo categorías que aplican a producto físico o servicio según el borrador del catálogo. */
  forItemType?: 'physical' | 'service';
};

const mapCategoryToOption = (category: CategorySource): CategoryOption => ({
  id: category.id,
  label: category.name,
  description: category.description ?? null,
  color: category.color ?? null,
  appliesTo: category.appliesTo,
  isRestricted: category.isRestricted,
  parentId: category.parentId ?? null,
});

const getActiveCategorySource = (
  categories: CategorySource[],
  fallbackCategories: CategorySource[] = [],
  forItemType?: 'physical' | 'service'
) => {
  const matchesItem = (category: CategorySource) =>
    forItemType == null || categoryAppliesToDraftItemType(category.appliesTo, forItemType);

  const activeCategories = categories.filter((category) => category.isActive && matchesItem(category));
  if (activeCategories.length > 0) {
    return activeCategories;
  }
  const fromFallback = fallbackCategories.filter((category) => category.isActive && matchesItem(category));
  return fromFallback.length > 0 ? fromFallback : [];
};

const findCategoryById = (categories: CategorySource[], id: string) =>
  categories.find((category) => normalizeProductCategoryId(category.id) === normalizeProductCategoryId(id));

export const buildCategoryOptions = (categories: CategorySource[], params: BuildCategoryOptionsParams = {}): CategoryOption[] => {
  const {
    fallbackCategories = [],
    includeAll = false,
    allLabel = 'Todos',
    allColor = '#6C5CE7',
    forItemType,
  } = params;

  const options = getActiveCategorySource(categories, fallbackCategories, forItemType).map(mapCategoryToOption);

  if (!includeAll) {
    return options;
  }

  return [{ id: 'all', label: allLabel, color: allColor }, ...options];
};

export const buildCategoryFilterOptions = (
  categories: CategorySource[],
  params: Pick<BuildCategoryOptionsParams, 'fallbackCategories' | 'forItemType'> & { allLabel?: string } = {}
): CategoryFilterOption[] => {
  const { fallbackCategories = [], allLabel = 'Todas', forItemType } = params;
  return [
    { id: 'all', label: allLabel },
    ...getActiveCategorySource(categories, fallbackCategories, forItemType).map((category) => ({
      id: category.id,
      label: category.name,
      appliesTo: category.appliesTo,
    })),
  ];
};

export const buildCategoryOptionsWithFallback = (
  categories: CategorySource[],
  selectedCategory?: string | null,
  params: Pick<BuildCategoryOptionsParams, 'fallbackCategories' | 'forItemType'> = {}
): CategoryOption[] => {
  const { fallbackCategories = [], forItemType } = params;
  const options = buildCategoryOptions(categories, { fallbackCategories, forItemType });

  if (!selectedCategory) {
    return options;
  }

  const normalizedSelectedCategory = normalizeProductCategoryId(selectedCategory);
  const alreadyIncluded = options.some((option) => normalizeProductCategoryId(option.id) === normalizedSelectedCategory);

  if (alreadyIncluded) {
    return options;
  }

  const sourceCategory = findCategoryById(categories, selectedCategory) ?? findCategoryById(fallbackCategories, selectedCategory);

  return [
    ...options,
    {
      id: selectedCategory,
      label: sourceCategory?.name ?? selectedCategory,
      description: sourceCategory?.description ?? null,
      color: sourceCategory?.color ?? null,
      appliesTo: sourceCategory?.appliesTo,
      isRestricted: sourceCategory?.isRestricted,
    },
  ];
};
